import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Card, CardContent, Button, Typography, Box, Tabs, Tab,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function SubscriptionDashboard() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState(null);
  const [couponDialog, setCouponDialog] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const [subRes, featRes, invRes, refRes] = await Promise.all([
        api.get('/subscriptions/current'),
        api.get('/subscriptions/features'),
        api.get('/subscriptions/invoices'),
        api.get('/subscriptions/referrals/generate').catch(() => null)
      ]);

      setSubscription(subRes.data?.subscription);
      setFeatures(subRes.data?.features || {});
      setInvoices(invRes.data?.invoices || []);
      if (refRes?.data?.referralCode) {
        setReferralCode(refRes.data.referralCode);
      }
    } catch (err) {
      console.error('Error loading subscription data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (planId) => {
    navigate(`/upgrade?planId=${planId}`);
  };

  const handleDowngrade = (planId) => {
    navigate(`/downgrade?planId=${planId}`);
  };

  const validateCoupon = async () => {
    try {
      const res = await api.post('/subscriptions/coupons/apply', { code: couponCode });
      setCouponValid(res.data?.coupon);
    } catch (err) {
      setCouponValid(false);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const res = await api.get(`/subscriptions/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.txt`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Error downloading invoice:', err);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Subscription & Billing</Typography>

      {/* Current Subscription Card */}
      {subscription ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">Current Plan</Typography>
            <Box sx={{ mt: 2 }}>
              <Typography>Plan: <strong>{subscription.plan_name || 'Free'}</strong></Typography>
              <Typography>Status: <strong>{subscription.status}</strong></Typography>
              <Typography>Expires: <strong>{new Date(subscription.end_date).toLocaleDateString()}</strong></Typography>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" color="primary" sx={{ mr: 1 }} onClick={() => handleUpgrade(2)}>
                  Upgrade
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => handleDowngrade(1)}>
                  Downgrade
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          You don't have an active subscription. Choose a plan to get started.
          <Button onClick={() => navigate('/pricing')} sx={{ ml: 2 }}>View Plans</Button>
        </Alert>
      )}

      {/* Tabs for Features, Invoices, Referrals */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Features" />
          <Tab label="Invoices" />
          <Tab label="Referrals" />
        </Tabs>
      </Box>

      {/* Features Tab */}
      {tabValue === 0 && (
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            {Object.entries(features).map(([key, feature]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1">{feature.name}</Typography>
                    <Typography variant="body2" color="textSecondary">{feature.description}</Typography>
                    {feature.limit_type !== 'UNLIMITED' && (
                      <Typography sx={{ mt: 1 }}>
                        Usage: {feature.current_usage || 0} / {feature.limit_value}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        mt: 1,
                        color: feature.is_enabled ? 'green' : 'red'
                      }}
                    >
                      {feature.is_enabled ? '✓ Enabled' : '✗ Disabled'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Invoices Tab */}
      {tabValue === 1 && (
        <Box sx={{ mt: 3 }}>
          {invoices.length > 0 ? (
            <Grid container spacing={2}>
              {invoices.map((invoice) => (
                <Grid item xs={12} key={invoice.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1">{invoice.invoice_number}</Typography>
                          <Typography variant="body2">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2">
                            Plan: {invoice.plan_name}
                          </Typography>
                          <Typography variant="h6" sx={{ mt: 1 }}>
                            ₹{invoice.total}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          onClick={() => downloadInvoice(invoice.id)}
                        >
                          Download
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">No invoices yet</Alert>
          )}
        </Box>
      )}

      {/* Referrals Tab */}
      {tabValue === 2 && (
        <Box sx={{ mt: 3 }}>
          {referralCode && (
            <Card>
              <CardContent>
                <Typography variant="h6">Your Referral Code</Typography>
                <TextField
                  fullWidth
                  value={referralCode}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 2 }}
                />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Share this code with friends to earn rewards
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    navigator.clipboard.writeText(referralCode);
                    alert('Copied to clipboard!');
                  }}
                  sx={{ mt: 2 }}
                >
                  Copy Code
                </Button>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Coupon Dialog */}
      <Dialog open={couponDialog} onClose={() => setCouponDialog(false)}>
        <DialogTitle>Apply Coupon Code</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Coupon Code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            sx={{ mt: 2 }}
          />
          {couponValid && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Coupon valid! Discount: {couponValid.discount_value}
            </Alert>
          )}
          {couponValid === false && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Invalid coupon code
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCouponDialog(false)}>Cancel</Button>
          <Button onClick={validateCoupon} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
